import React from "react";
import { createPortal } from "react-dom";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import useOutsideClickTrigger from "../../hooks/useOutsideClickTrigger";
import CloseButton from "../LifeAtButton/Buttons/CloseButton";
import "./LifeAtModal.scss";

export type ModalNotification = {
    type: "error" | "warn" | "info";
    message: string;
};

export interface LifeAtModalProps {
    readonly title?: string;
    readonly subtitle?: string;
    readonly children: JSX.Element | JSX.Element[];
    readonly notifications?: ModalNotification[];
    readonly isOpen: boolean;
    readonly closeModal: () => void;
    readonly centerContent?: boolean;
    /**
     * If the modal should not be closeable. This also affects the background highlighting of
     * the modal, causing it to darken even further past the default.
     */
    readonly preventClose?: boolean;
    readonly className?: string;
}
export default function LifeAtModal(props: LifeAtModalProps) {
    const modalRef = useOutsideClickTrigger(props.preventClose ? () => {} : props.closeModal);

    const { lightMode }: AppearanceContextType = useAppearanceContext();

    const modalNotificationComponents = props.notifications?.map((n: ModalNotification) => {
        return (
            <span key={`modal-notification-${n.message}`} className={`modal-notification ${n.type}`}>
                {getNotificationEmoji(n.type)} {n.message}
            </span>
        );
    });

    // wrapper allows us to control the background appearance after the modal is opened
    const modalContent = (
        <div className={`lifeat-modal-wrapper ${props.className} ${props.preventClose ? "spotlight" : ""}`}>
            <div
                className={`lifeat-modal ${lightMode ? "light" : "dark"}`}
                ref={modalRef}
                // Note: stop mouse down event propagation so that mouse down event doesn't bubble up to the document.
                // This is to handle the useOutsideClickHandler in MainDropdownMenu closing if user clicks inside the account modal
                onMouseDown={(e) => e.stopPropagation()}
            >
                {/* Header elements */}
                <div
                    className={`modal-body modal-header ${props.preventClose ? "prevent-close" : ""} ${
                        props.centerContent ? "centered" : ""
                    }`}
                >
                    <span className={`modal-title ${props.centerContent ? "centered" : ""}`}>{props.title}</span>
                    {!props.preventClose && <CloseButton handleClose={() => props.closeModal()} />}
                </div>

                {/* Subheader elements */}
                {props.subtitle && (
                    <div className={`modal-body modal-subheader`}>
                        <span className={`modal-subtitle ${props.centerContent ? "centered" : ""}`}>
                            {props.subtitle}
                        </span>
                    </div>
                )}

                {/* Content is generated via composition. */}
                <div className={`modal-body modal-content ${props.centerContent ? "centered" : ""}`}>
                    {props.children}
                </div>

                <div className={"modal-body modal-notifications"}>{modalNotificationComponents}</div>
            </div>
        </div>
    );

    // Note: Portal is created to render the modal into the target container (div with id "modal-portal")
    // This will be above the top navigation bar, sidebars, and overlay buttons in the DOM
    const portalDiv = document.getElementById("modal-portal");
    const canRenderPortal = props.isOpen && portalDiv;

    return canRenderPortal ? createPortal(modalContent, portalDiv) : null;
}
LifeAtModal.defaultProps = { notifications: [] };

const getNotificationEmoji = (notificationType: string) => {
    let emoji;
    switch (notificationType) {
        case "error": {
            emoji = "❗";
            break;
        }
        case "warn": {
            emoji = "⚠️";
            break;
        }
        case "info": {
            emoji = "ℹ️";
            break;
        }
        default: {
            emoji = "";
            break;
        }
    }
    return emoji;
};
